import { useCallback, useEffect, useRef, useState } from "react";
import { API, postWithRetry } from "@/lib/api";
import { sumNumbersInText, formatNumber } from "@/lib/mr";

/**
 * Ellipsis-tap reveal for the Type/Draw input modes, plus the triple-tap
 * escape gesture and the long-press silent re-roll. Owns the reveal state
 * and the per-mode computation:
 *   - type : local sum if pure numbers, otherwise POST /api/text
 *   - draw : POST /api/vision (cached until the drawing changes)
 *
 * (The Speak mode was retired in the Feb 2026 restructure.)
 *
 * The dispatch on reveal depends on `revealStyle`:
 *   - "peek"      — set revealValue; Trick renders it subliminally in the date line
 *   - "scroll"    — hand the value off to `onScrollAppend` so the caller
 *                   can write it into the note body below the fold
 *   - "whisper"   — Trick subscribes to revealValue and routes it to TTS
 *
 * @param {{
 *   mode: "draw" | "type",
 *   revealStyle: "peek" | "scroll" | "whisper",
 *   body: string,
 *   canvasRef: React.RefObject<{ isEmpty?: () => boolean, getDataURL: (s: number) => string }>,
 *   multiQuestion: boolean,
 *   onScrollAppend?: (value: string, caption: string) => void,
 *   onExit?: () => void,
 * }} opts
 */
export default function useReveal({
  mode,
  revealStyle,
  body,
  canvasRef,
  multiQuestion = false,
  onScrollAppend,
  onExit,
}) {
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealValue, setRevealValue] = useState("");
  const [revealCaption, setRevealCaption] = useState("");

  // Vision result is cached until the drawing visibly changes.
  const visionFetchedRef = useRef(false);
  const invalidateVision = useCallback(() => {
    visionFetchedRef.current = false;
  }, []);

  // ---- Type mode -----------------------------------------------------------
  const computeTypeReveal = useCallback(async () => {
    const text = body.trim();
    if (!text) {
      setRevealValue("—");
      setRevealCaption("");
      return { value: "—", caption: "" };
    }
    const stripped = text.replace(/[\d\s+\-*/=().,]/g, "");
    // Skip the local sum short-circuit when multi-question is on so the
    // backend can parse the numbered prompts correctly.
    if (!multiQuestion && stripped.length <= 2) {
      const result = sumNumbersInText(text);
      if (result && result.numbers.length) {
        const value = formatNumber(result.total);
        const caption = result.numbers.length === 1
          ? ""
          : `${result.numbers.map(formatNumber).join(" + ")} =`;
        setRevealValue(value);
        setRevealCaption(caption);
        return { value, caption };
      }
    }
    setRevealLoading(true);
    setRevealValue("");
    setRevealCaption("");
    try {
      const res = await postWithRetry(`${API}/text`, { text, multi: multiQuestion }, { timeout: 60000 });
      const { kind, numbers = [], total, answer: ans } = res.data || {};
      let value = "—", caption = "";
      if (kind === "sum" && total !== null && total !== undefined) {
        value = formatNumber(total);
        caption = numbers.length > 1 ? `${numbers.map(formatNumber).join(" + ")} =` : "";
      } else if (kind === "answer" && ans) {
        value = ans;
      }
      setRevealValue(value);
      setRevealCaption(caption);
      return { value, caption };
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed";
      setRevealValue("—");
      setRevealCaption(typeof msg === "string" ? msg.slice(0, 60) : "Failed");
      return { value: "—", caption: "" };
    } finally {
      setRevealLoading(false);
    }
  }, [body, multiQuestion]);

  // ---- Draw mode -----------------------------------------------------------
  const computeDrawReveal = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.isEmpty?.()) {
      setRevealValue("—");
      setRevealCaption("");
      return { value: "—", caption: "" };
    }
    if (visionFetchedRef.current && revealValue) {
      return { value: revealValue, caption: revealCaption };
    }
    setRevealLoading(true);
    setRevealValue("");
    setRevealCaption("");
    try {
      const dataURL = canvas.getDataURL(1024);
      const res = await postWithRetry(`${API}/vision`, { image: dataURL, multi: multiQuestion }, {
        timeout: 60000,
      });
      const { kind, numbers = [], total, answer: ans } = res.data || {};
      let value = "—", caption = "";
      if (kind === "sum" && total !== null && total !== undefined) {
        value = formatNumber(total);
        caption = numbers.length > 1 ? `${numbers.map(formatNumber).join(" + ")} =` : "";
        visionFetchedRef.current = true;
      } else if (kind === "answer" && ans) {
        value = ans;
        visionFetchedRef.current = true;
      }
      setRevealValue(value);
      setRevealCaption(caption);
      return { value, caption };
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Vision failed";
      setRevealValue("—");
      setRevealCaption(typeof msg === "string" ? msg.slice(0, 60) : "Vision failed");
      return { value: "—", caption: "" };
    } finally {
      setRevealLoading(false);
    }
  }, [canvasRef, revealValue, revealCaption, multiQuestion]);

  // ---- Gestures ------------------------------------------------------------
  const tapTimes = useRef([]);
  const handleDateTap = useCallback(() => {
    const now = Date.now();
    tapTimes.current = [...tapTimes.current, now].filter((t) => now - t < 700);
    if (tapTimes.current.length >= 3) {
      tapTimes.current = [];
      onExit?.();
    }
  }, [onExit]);

  /**
   * Compute the per-mode answer and stash it into `revealValue` /
   * `revealCaption`. Called by the ellipsis tap on the toolbar.
   * In peek mode the caller renders revealValue subliminally in the date
   * line; in scroll-peek mode the value is piped into the note body.
   */
  const triggerReveal = useCallback(async (opts = {}) => {
    // Toggle off: if a reveal is already showing, the magician taps again
    // to hide it (and bring back the date/time). Loading-state reveals
    // are not toggled away — they finish first. silentRedo passes
    // {force:true} to always recompute regardless of current state.
    if (!opts.force && !revealLoading && revealValue && revealValue !== "") {
      setRevealValue("");
      setRevealCaption("");
      return;
    }

    let result;
    if (mode === "draw") {
      result = await computeDrawReveal();
    } else {
      result = await computeTypeReveal();
    }

    if (revealStyle === "scroll" && result?.value && result.value !== "—") {
      onScrollAppend?.(result.value, result.caption || "");
    }
  }, [mode, revealStyle, onScrollAppend, computeTypeReveal, computeDrawReveal, revealLoading, revealValue]);

  /**
   * Silent redo — re-runs the latest computation in case the spectator
   * changed their input or the magician misheard. For Draw we explicitly
   * invalidate the cached Vision result so the API is hit again with the
   * (possibly updated) drawing. Forces compute (no toggle-off).
   */
  const silentRedo = useCallback(async () => {
    if (mode === "draw") visionFetchedRef.current = false;
    await triggerReveal({ force: true });
  }, [mode, triggerReveal]);

  const pressTimer = useRef(null);
  const startPress = useCallback(() => {
    pressTimer.current = setTimeout(() => {
      silentRedo();
    }, 450);
  }, [silentRedo]);

  const endPress = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  // Clear any pending long-press on unmount
  useEffect(() => () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
  }, []);

  return {
    revealLoading,
    revealValue,
    revealCaption,
    startPress,
    endPress,
    handleDateTap,
    invalidateVision,
    triggerReveal,
    silentRedo,
  };
}
